import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Text, Button, Spinner, Heading, Octicon } from "@primer/react";
import { CheckIcon, XIcon, CreditCardIcon, ArrowRightIcon } from "@primer/octicons-react";
import { DashboardLayout } from "../components/DashboardLayout";
import { useAuth } from "../contexts/AuthContext";
import { API_ENDPOINTS } from "../config/api";

export default function Success() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing your payment...');
  const [details, setDetails] = useState<{
    tokens?: string;
    amount?: string;
  }>({});

  // Function to check transaction status
  const checkTransactionStatus = async (checkoutId: string) => {
    if (!user) return null;

    try {
      const response = await fetch(API_ENDPOINTS.TRANSACTION_STATUS(checkoutId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${await user.getIdToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error checking transaction status:', error);
      return null;
    }
  };

  useEffect(() => {
    const processPaymentSuccess = async () => {
      const sessionId = searchParams.get('session_id'); // Polar checkout ID
      const customerSessionToken = searchParams.get('customer_session_token'); // Polar customer session token

      console.log('Success page loaded with params:', {
        sessionId,
        customerSessionToken,
        allParams: Object.fromEntries(searchParams.entries())
      });

      // Validate parameters - we need at least the session ID
      if (!sessionId || !user) {
        setStatus('error');
        setMessage('Invalid payment parameters. Please contact support if you believe this is an error.');
        return;
      }

      try {
        // Check transaction status with Polar using session ID
        console.log('Checking transaction status for session:', sessionId);

        // Try to check transaction status
        const transactionStatus = await checkTransactionStatus(sessionId);

        console.log('Transaction status:', transactionStatus);

        if (transactionStatus) {
          if (transactionStatus.success && transactionStatus.data?.transaction?.status === 'succeeded') {
            // Payment confirmed by Polar
            setStatus('success');
            setMessage(transactionStatus.message || 'Payment confirmed and processed successfully!');
            setDetails({
              tokens: transactionStatus.data?.wallet?.tokens_added?.toLocaleString() || '0',
              amount: `$${transactionStatus.data?.transaction?.amount?.toFixed(2) || '0.00'}`,
            });
          } else if (transactionStatus.data?.transaction?.status === 'succeeded') {
            // Payment succeeded but not yet processed by our system
            setStatus('processing');
            setMessage(`Payment status: ${transactionStatus.data.transaction.status}. Processing your tokens...`);

            // Try again after a delay
            setTimeout(async () => {
              const retryStatus = await checkTransactionStatus(sessionId);
              if (retryStatus?.success && retryStatus.data?.transaction?.status === 'succeeded') {
                setStatus('success');
                setMessage(retryStatus.message || 'Payment confirmed and processed successfully!');
                setDetails({
                  tokens: retryStatus.data?.wallet?.tokens_added?.toLocaleString() || '0',
                  amount: `$${retryStatus.data?.transaction?.amount?.toFixed(2) || '0.00'}`,
                });
              } else {
                setStatus('error');
                setMessage('Payment verification failed. Please contact support with your payment confirmation.');
              }
            }, 3000);
          } else {
            // Payment not completed yet, show processing
            setStatus('processing');
            setMessage(`Payment status: ${transactionStatus.data?.transaction?.status || 'processing'}. Please wait...`);

            // Try again after a delay
            setTimeout(async () => {
              const retryStatus = await checkTransactionStatus(sessionId);
              if (retryStatus?.success && retryStatus.data?.transaction?.status === 'succeeded') {
                setStatus('success');
                setMessage(retryStatus.message || 'Payment confirmed and processed successfully!');
                setDetails({
                  tokens: retryStatus.data?.wallet?.tokens_added?.toLocaleString() || '0',
                  amount: `$${retryStatus.data?.transaction?.amount?.toFixed(2) || '0.00'}`,
                });
              } else {
                setStatus('error');
                setMessage('Payment verification failed. Please contact support with your payment confirmation.');
              }
            }, 5000);
          }
        } else {
          // Status check failed, show error
          setStatus('error');
          setMessage('Unable to verify payment status. Please contact support with your payment confirmation.');
        }

        // Auto navigation removed - user can manually navigate when ready
      } catch (error) {
        console.error('Error processing payment success:', error);
        setStatus('error');
        setMessage('There was an issue processing your payment. Your payment was successful, but there may be a delay in crediting your account. Please contact support if the tokens don\'t appear in your account within a few minutes.');
      }
    };

    processPaymentSuccess();
  }, [searchParams, user, navigate]);

  const handleReturnToDashboard = () => {
    navigate('/dashboard/usage');
  };

  return (
    <DashboardLayout>
      <Box
        className="app-page payment-success-page"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100%',
          background: '#ffffff',
          // p: 4,
        }}
      >
        <Box
          className="app-panel"
          sx={{
            maxWidth: 600,
            width: '100%',
            textAlign: 'center',
            p: 0,
            borderRadius: 4,
            bg: 'white',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          {/* <Box
            sx={{
              bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              p: 4,
              color: 'white',
            }}
          >
            <Heading sx={{ fontSize: 4, fontWeight: 'bold', mb: 2, color: 'white' }}>
              Payment Status
            </Heading>
            <Text sx={{ fontSize: 2, opacity: 0.9 }}>
              {status === 'processing' && 'Processing your payment...'}
              {status === 'success' && 'Payment completed successfully!'}
              {status === 'error' && 'Payment verification failed'}
            </Text>
          </Box> */}

          {/* Content */}
          <Box sx={{ p: 6 }}>
            {status === 'processing' && (
              <>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bg: '#f0ebff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 4,
                    position: 'relative',
                  }}
                >
                  <Spinner size="large" />
                </Box>
                <Heading sx={{ fontSize: 3, fontWeight: 'bold', mb: 3, color: 'fg.default' }}>
                  Processing Payment
                </Heading>
                <Text sx={{ color: 'fg.muted', fontSize: 2, mb: 4 }}>
                  {message}
                </Text>
                <Box
                  sx={{
                    p: 3,
                    bg: 'canvas.subtle',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'border.default',
                  }}
                >
                  <Text sx={{ fontSize: 1, color: 'fg.muted' }}>
                    Please wait while we verify your payment with our payment processor...
                  </Text>
                </Box>
              </>
            )}

            {status === 'success' && (
              <>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bg: '#e9f6ee',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 4,
                    color: '#237a42',
                  }}
                >
                  <CheckIcon size={40} />
                </Box>
                <Heading sx={{ fontSize: 4, fontWeight: 'bold', mb: 3, color: 'success.fg' }}>
                  Payment Successful
                </Heading>
                <Text sx={{ color: 'fg.muted', fontSize: 2, mb: 4 }}>
                  {message}
                </Text>

                {details.tokens && details.amount && (
                  <Box
                    sx={{
                      p: 4,
                      bg: '#f7f7f6',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'success.muted',
                      mb: 4,
                      mt: 2,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bg: 'success.emphasis',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CreditCardIcon size={20} />
                    </Box>
                    <Text sx={{ fontSize: 1, color: 'fg.muted', mb: 2, fontWeight: 'bold' }}>
                      Payment Summary
                    </Text>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                      <Text sx={{ fontSize: 5, fontWeight: 'bold', color: 'success.fg', mr: 2 }}>
                        {details.tokens}
                      </Text>
                      <Text sx={{ fontSize: 2, color: 'fg.muted' }}>
                        tokens added
                      </Text>
                    </Box>
                    <Text sx={{ fontSize: 1, color: 'fg.muted' }}>
                      Amount paid: <Text sx={{ fontWeight: 'bold', color: 'fg.default' }}>{details.amount}</Text>
                    </Text>
                  </Box>
                )}

                <Box
                  sx={{
                    p: 3,
                    bg: 'canvas.subtle',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'border.default',
                    mb: 4,
                  }}
                >
                  <Text sx={{ fontSize: 1, color: 'fg.muted', mb: 2 }}>
                    <Octicon icon={ArrowRightIcon} sx={{ mr: 1 }} />
                    Your payment has been processed successfully!
                  </Text>
                </Box>

                <Button
                  onClick={handleReturnToDashboard}
                  variant="primary"
                  className="app-primary-button"
                  sx={{
                    bg: '#1d1d1f',
                    border: 'none',
                    px: 4,
                    py: 2,
                    fontSize: 2,
                    fontWeight: 'bold',
                    '&:hover': {
                      bg: '#000000',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Octicon icon={ArrowRightIcon} sx={{ mr: 2 }} />
                  Go to Dashboard
                </Button>
              </>
            )}

            {status === 'error' && (
              <>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bg: '#fff2f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 4,
                    color: '#d92d20',
                  }}
                >
                  <XIcon size={40} />
                </Box>
                <Heading sx={{ fontSize: 4, fontWeight: 'bold', mb: 3, color: 'danger.fg' }}>
                  Payment Verification Failed
                </Heading>
                <Text sx={{ color: 'fg.muted', fontSize: 2, mb: 4 }}>
                  {message}
                </Text>

                <Box
                  sx={{
                    p: 4,
                    bg: '#fff2f0',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'danger.muted',
                    mb: 4,
                  }}
                >
                  <Text sx={{ fontSize: 1, fontWeight: 'bold', mb: 2, color: 'danger.fg' }}>
                    Need Help?
                  </Text>
                  <Text sx={{ fontSize: 1, color: 'fg.muted', mb: 3 }}>
                    Don't worry! If you completed the payment, your tokens will be credited shortly.
                    If you continue to experience issues, please contact our support team.
                  </Text>
                  <Text sx={{ fontSize: 1, color: 'fg.muted' }}>
                    Email: <Text sx={{ fontWeight: 'bold', color: 'accent.fg' }}>support@forgepdf.io</Text>
                  </Text>
                </Box>

                <Button
                  onClick={handleReturnToDashboard}
                  variant="primary"
                  className="app-primary-button"
                  sx={{
                    bg: '#1d1d1f',
                    border: 'none',
                    px: 4,
                    py: 2,
                    fontSize: 2,
                    fontWeight: 'bold',
                    '&:hover': {
                      bg: '#000000',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Octicon icon={ArrowRightIcon} sx={{ mr: 2 }} />
                  Return to Dashboard
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </DashboardLayout>
  );
}
